import React, { useMemo } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { ChevronRight, ExternalLink } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  selectItemsByValue,
} from '@ui/components/ui/select';
import { ProductQualityAudit } from '@data';
import { Card, SectionHeading, SectionLabel } from '@app/components/core';
import { useProductViewer } from '../../products/productDetail/productViewer';
import { ChangesSection, DeltaTone, StandingDelta, deltaTone } from './QualityChanges';
import { useQualityComparison } from './useQualityComparison';
import {
  AuditChanges,
  AuditCheck,
  AuditEvidence,
  AuditSection,
  AuditStanding,
  AuditStat,
  AuditVerdict,
  QualityAuditBody,
  auditBody,
  qualityPeriodLabel,
} from './sources/quality';

/**
 * The audit's verdicts in the dashboard's own colour vocabulary (see `STATUS_META` in sources/types):
 * green is at target, amber is short of it, red is absent. The two non-failures stay neutral so they
 * never read as a problem — `unverified` keeps a tint only so it is distinguishable in the tally bar.
 */
const VERDICT: Record<AuditVerdict, { text: string; fill: string; tint: string }> = {
  met: { text: 'text-emerald-600 dark:text-emerald-400', fill: 'bg-emerald-500', tint: 'bg-emerald-500/10' },
  partial: { text: 'text-amber-600 dark:text-amber-400', fill: 'bg-amber-500', tint: 'bg-amber-500/10' },
  gap: { text: 'text-red-600 dark:text-red-400', fill: 'bg-red-500', tint: 'bg-red-500/10' },
  na: { text: 'text-muted-foreground', fill: 'bg-muted-foreground/30', tint: 'bg-muted' },
  unverified: { text: 'text-sky-600 dark:text-sky-400', fill: 'bg-sky-500', tint: 'bg-sky-500/10' },
};

/** A check's standing, which is what the tab leads with. Short of target is amber, never red, to a client. */
const STANDING: Record<AuditStanding, { text: string; fill: string; tint: string }> = {
  meetsTarget: VERDICT.met,
  atStandard: { text: 'text-sky-600 dark:text-sky-400', fill: 'bg-sky-500', tint: 'bg-sky-500/10' },
  improvementPlanned: VERDICT.partial,
  notAssessed: { text: 'text-muted-foreground', fill: 'bg-muted-foreground/50', tint: 'bg-muted' },
  notApplicable: { text: 'text-muted-foreground/70', fill: 'bg-muted-foreground/20', tint: 'bg-muted/50' },
};

const NEUTRAL = { text: 'text-foreground', fill: 'bg-muted-foreground/30', tint: 'bg-muted' };

const verdictStyle = (
  verdict: AuditVerdict | null,
  standing?: AuditStanding | null,
): { text: string; fill: string; tint: string } =>
  (standing && STANDING[standing]) || (verdict && VERDICT[verdict]) || NEUTRAL;

/**
 * Quarter picker for the Quality and Productivity tabs. Hosts hold the value and pass it back down as the tab's
 * `report`, the same arrangement as the Team tab's range and the Tooling tab's year.
 *
 * Hidden until there is a second audit to switch to: one quarter of history is not a choice, and a
 * select with a single option reads as though other quarters exist behind it.
 */
export const QualityPeriodSelect = <T extends { year: number; quarter: number } = ProductQualityAudit>({
  value,
  onChange,
  audits,
}: {
  value: T | null;
  onChange: (audit: NoInfer<T>) => void;
  audits: T[];
}): JSX.Element | null => {
  if (audits.length < 2 || !value) return null;

  const options = audits.map(audit => ({ value: qualityPeriodLabel(audit), label: qualityPeriodLabel(audit) }));

  return (
    <Select
      value={qualityPeriodLabel(value)}
      onValueChange={next => {
        const picked = audits.find(audit => qualityPeriodLabel(audit) === next);
        if (picked) onChange(picked);
      }}
      items={selectItemsByValue(options)}
    >
      <SelectTrigger size="sm" className="bg-card shadow-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="min-w-0">
        {options.map(option => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const NO_COMPARISON = 'none';

/** Which earlier quarter the shown audit is compared with, off by default. `audits` are the earlier quarters only. */
const QualityCompareSelect = ({
  value,
  onChange,
  audits,
  failed,
}: {
  value: string | undefined;
  onChange: (compareTo: string | undefined) => void;
  audits: ProductQualityAudit[];
  /** The picked comparison could not be read, said here rather than over the quarter's own audit. */
  failed: boolean;
}): JSX.Element => {
  const { t } = useTranslation();

  const options = [
    { value: NO_COMPARISON, label: t('quality.compare.none') },
    ...audits.map(audit => ({ value: qualityPeriodLabel(audit), label: qualityPeriodLabel(audit) })),
  ];

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span className="text-sm text-muted-foreground">{t('quality.compare.label')}</span>
      <Select
        value={value ?? NO_COMPARISON}
        onValueChange={next => onChange(!next || next === NO_COMPARISON ? undefined : String(next))}
        items={selectItemsByValue(options)}
      >
        <SelectTrigger size="sm" className="bg-card shadow-xs" aria-label={t('quality.compare.label')}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="min-w-0">
          {options.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {failed && (
        <span role="status" className="text-xs text-muted-foreground">
          {t('quality.compare.failed', { period: value })}
        </span>
      )}
    </span>
  );
};

const Pill = ({
  verdict,
  standing,
  children,
}: {
  verdict: AuditVerdict | null;
  standing?: AuditStanding | null;
  children: React.ReactNode;
}): JSX.Element => {
  const style = verdictStyle(verdict, standing);
  return (
    <span
      className={cx(
        'inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-xs font-semibold',
        style.tint,
        style.text,
      )}
    >
      {children}
    </span>
  );
};

/** A check's standing; staff see the audit's raw state only when no standing could be derived from it. */
const CheckStanding = ({ check }: { check: AuditCheck }): JSX.Element | null => {
  const { t } = useTranslation();
  const { isClient } = useProductViewer();
  if (!check.standing) return isClient ? null : <Pill verdict={check.verdict}>{check.verdict}</Pill>;

  return (
    <Pill verdict={check.verdict} standing={check.standing}>
      {t(`quality.standing.${check.standing}`)}
    </Pill>
  );
};

/** A reference the audit traced a finding to: a file path, a command, a query. */
const Reference = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <code className="break-all rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
    {children}
  </code>
);

/** A floor is a state, so it reads as the standing it guarantees; staff see the raw value only when it maps to none. */
const FLOOR_STANDING: Record<string, AuditStanding> = { met: 'meetsTarget', partial: 'atStandard' };

const FloorValue = ({ floor }: { floor: string }): JSX.Element | null => {
  const { t } = useTranslation();
  const { isClient } = useProductViewer();
  const standing = FLOOR_STANDING[floor];
  if (standing) return <>{t(`quality.standing.${standing}`)}</>;
  return isClient ? null : <Reference>{floor}</Reference>;
};

const DELTA_TONE: Record<DeltaTone, string> = {
  good: VERDICT.met.text,
  bad: VERDICT.gap.text,
  neutral: 'text-muted-foreground',
};

// Figures keep the dashboard's stat proportions; only the number takes the verdict colour, so a strip
// of tiles reads as one row of measurements rather than a traffic light.
const StatTile = ({ stat, delta, since }: { stat: AuditStat; delta?: number; since?: string }): JSX.Element => (
  <div className="rounded-xl border border-border bg-card p-4">
    <p className={cx('text-2xl font-bold', verdictStyle(stat.verdict, stat.standing).text)}>
      {stat.value}
      {!!delta && !!since && !!stat.standing && (
        <StandingDelta delta={delta} since={since} className={DELTA_TONE[deltaTone(stat.standing, delta)]} />
      )}
    </p>
    <p className="mt-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">{stat.label}</p>
  </div>
);

/** The tally as one bar, each standing sized by its share of the checks. */
const TallyBar = ({ breakdown }: { breakdown: NonNullable<AuditSection['breakdown']> }): JSX.Element => (
  <div className="flex h-2 overflow-hidden rounded-full">
    {breakdown.map(part => (
      <span
        key={part.standing ?? 'none'}
        className={verdictStyle(null, part.standing).fill}
        style={{ flex: part.count }}
        title={`${part.label ?? part.standing}: ${part.count}`}
      />
    ))}
  </div>
);

const Legend = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <p className="text-xs leading-relaxed text-muted-foreground">{children}</p>
);

/** Only the tally carries a breakdown, so only its figures get a delta against the compared quarter. */
const deltaOf = (section: AuditSection, stat: AuditStat, changes: AuditChanges | null): number | undefined =>
  section.breakdown && stat.standing
    ? changes?.standings.find(part => part.standing === stat.standing)?.delta
    : undefined;

const HeadlineSection = ({
  section,
  changes,
}: {
  section: AuditSection;
  changes: AuditChanges | null;
}): JSX.Element => (
  <Card className="flex flex-col gap-4">
    <p className="text-base font-semibold text-foreground">{section.title}</p>
    {!!section.stats.length && (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {section.stats.map(stat => (
          <StatTile key={stat.label} stat={stat} delta={deltaOf(section, stat, changes)} since={changes?.since} />
        ))}
      </div>
    )}
    {!!section.breakdown?.length && <TallyBar breakdown={section.breakdown} />}
    {section.legends.map(legend => (
      <Legend key={legend}>{legend}</Legend>
    ))}
    {/* No heading of our own: the audit's last legend already introduces this list. */}
    {!!section.opportunities.length && (
      <div className="flex flex-col gap-2">
        {section.opportunities.map(opportunity => (
          <div key={opportunity.route ?? opportunity.score} className="flex flex-wrap items-center gap-2 text-sm">
            <Pill verdict={opportunity.verdict}>{opportunity.score}</Pill>
            <Reference>{opportunity.route}</Reference>
            <span className="text-muted-foreground">{opportunity.detail}</span>
          </div>
        ))}
      </div>
    )}
  </Card>
);

const EvidenceList = ({ items }: { items: AuditEvidence[] }): JSX.Element => (
  <ul className="flex flex-col gap-2">
    {items.map(item => (
      <li key={`${item.title}${item.reference}`} className="text-sm text-muted-foreground">
        {!!item.title && <span className="font-medium text-foreground">{item.title}</span>}
        {!!item.detail && <span>{item.title ? ` — ${item.detail}` : item.detail}</span>}
        {!!item.reference && (
          <span className="mt-1 block">
            <Reference>{item.reference}</Reference>
          </span>
        )}
      </li>
    ))}
  </ul>
);

/** One label/value row of a check's dossier. */
const Field = ({ label, children }: { label: string; children: React.ReactNode }): JSX.Element => (
  <div className="flex flex-col gap-1 sm:flex-row sm:gap-4">
    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:w-24 sm:shrink-0 sm:pt-0.5">
      {label}
    </p>
    <div className="min-w-0 flex-1 text-sm text-foreground">{children}</div>
  </div>
);

const CheckDetail = ({ check, showFloor = true }: { check: AuditCheck; showFloor?: boolean }): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3 border-t border-border pt-3">
      {!!check.target && <Field label={t('quality.target')}>{check.target}</Field>}
      {!!check.finding && <Field label={t('quality.finding')}>{check.finding}</Field>}
      {!!check.evidence.length && (
        <Field label={t('quality.evidence')}>
          <EvidenceList items={check.evidence} />
        </Field>
      )}
      {!!check.nextStep && (
        <Field label={t('quality.nextStep')}>
          <span className="font-medium">{check.nextStep}</span>
        </Field>
      )}
      {!!check.reason && <Field label={t('quality.reason')}>{check.reason}</Field>}
      {!!check.floor && showFloor && (
        <Field label={t('quality.floor')}>
          <FloorValue floor={check.floor} />
        </Field>
      )}
      {!!check.issueUrl && (
        <Field label={t('quality.issue')}>
          <a
            href={check.issueUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-medium text-foreground underline underline-offset-2"
          >
            {check.issueUrl.replace(/^https:\/\/github\.com\//, '')}
            <ExternalLink className="size-3.5" />
          </a>
        </Field>
      )}
    </div>
  );
};

/**
 * A check, collapsed to its standing and its one-line remediation.
 *
 * Native `<details>` rather than held state: there are 52 of these, they open independently, and the
 * browser already gets the keyboard and accessibility behaviour right.
 */
const CheckRow = ({ check }: { check: AuditCheck }): JSX.Element => {
  const { t } = useTranslation();
  return (
    <details className="group border-b border-border last:border-b-0">
      <summary className="flex cursor-pointer list-none items-start gap-3 py-3 hover:bg-muted/40 [&::-webkit-details-marker]:hidden">
        <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2">
            {/* A dot rather than the word: mandatory is a property of the check, not its result. */}
            {check.mandatory && (
              <span className="text-foreground" title={t('quality.mandatory')} aria-label={t('quality.mandatory')}>
                ●
              </span>
            )}
            <span className="text-sm font-medium text-foreground">{check.name}</span>
            <Reference>{check.slug}</Reference>
          </span>
          {!!check.summary && <span className="text-xs text-muted-foreground">{check.summary}</span>}
        </span>
        <CheckStanding check={check} />
      </summary>
      <div className="pb-4 pl-7 pr-2">
        <CheckDetail check={check} />
      </div>
    </details>
  );
};

/** A check that fell below its floor. Shown up front, expanded, because it has to be scheduled. */
const RoadmapCard = ({ item }: { item: AuditCheck }): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <CheckStanding check={item} />
        {!!item.floor && (
          <span className="text-xs text-muted-foreground">
            {t('quality.floorIs')} <FloorValue floor={item.floor} />
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-base font-semibold text-foreground">{item.name}</p>
        <Reference>{item.slug}</Reference>
      </div>
      <CheckDetail check={item} showFloor={false} />
    </Card>
  );
};

export const Notice = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <p className="rounded-xl border border-dashed border-border px-3 py-10 text-center text-sm text-muted-foreground">
    {children}
  </p>
);

const AuditView = ({ audit }: { audit: QualityAuditBody }): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-4">
      {audit.sections.map(section => (
        <HeadlineSection key={section.title} section={section} changes={audit.changes} />
      ))}

      {!!audit.changes && <ChangesSection changes={audit.changes} period={audit.period} Pill={Pill} />}

      {!!audit.roadmap.length && (
        <>
          <SectionLabel>{t('quality.roadmapSection', { count: audit.roadmap.length })}</SectionLabel>
          {audit.roadmap.map(item => (
            <RoadmapCard key={item.slug} item={item} />
          ))}
        </>
      )}

      {!!audit.categories.length && (
        <>
          <SectionLabel>{t('quality.checksSection')}</SectionLabel>
          {audit.categories.map(category => (
            <Card key={category.title}>
              <p className="mb-1 text-base font-semibold text-foreground">{category.title}</p>
              {category.checks.map(check => (
                <CheckRow key={check.slug} check={check} />
              ))}
            </Card>
          ))}
        </>
      )}

      {!!audit.footer && <Legend>{audit.footer}</Legend>}
    </div>
  );
};

/**
 * The quarterly quality audit for a product. Unlike the other tabs, nothing here is assembled from
 * Phill's data: the audit is filed by the quality-audit skill on its own cadence, and this tab is
 * where the client reads it rather than digging the file out of an email.
 *
 * The audit ships as its own styled document. Only its content is stored — the layout is Phill's, so
 * the tab reads as part of the product view rather than a second site embedded in it.
 */
const QualityTab = ({
  productId,
  report,
  isUnavailable,
  header,
  actions,
}: {
  productId?: string;
  report: ProductQualityAudit | null;
  /** Stands in for the audit's own heading, such as the host's audit picker. */
  header?: React.ReactNode;
  /** Controls on the heading's line when there is no header, such as the quarter picker. */
  actions?: React.ReactNode;
  /** The index could not be read, so whether this product has an audit is unknown rather than no. */
  isUnavailable?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const label = report ? qualityPeriodLabel(report) : '';

  const { compareTo, setCompareTo, older, isShown, isFailed, shown } = useQualityComparison(productId, report);
  const { data, isLoading, isError, error } = shown;
  const audit = useMemo(() => auditBody(data?.audit), [data]);

  const bodyUnavailable =
    (error as { response?: { data?: { reason?: string } } })?.response?.data?.reason === 'githubUnreachable';

  let body = <Notice>{t('quality.loading')}</Notice>;
  if (!report) body = <Notice>{isUnavailable ? t('quality.unavailable') : t('quality.empty')}</Notice>;
  else if (bodyUnavailable) body = <Notice>{t('quality.unavailable')}</Notice>;
  else if (isError) body = <Notice>{t('quality.failed', { period: label })}</Notice>;
  else if (!isLoading && audit) body = <AuditView audit={audit} />;

  // Nothing older to compare with, so no picker rather than one offering only "No comparison".
  const compare = older.length ? (
    <QualityCompareSelect value={compareTo} onChange={setCompareTo} audits={older} failed={isFailed} />
  ) : null;

  return (
    <section className="flex flex-col gap-4">
      <div>
        {header ?? (
          <SectionHeading
            action={
              compare ? (
                <div className="flex flex-wrap items-center gap-3">
                  {actions}
                  {compare}
                </div>
              ) : (
                actions
              )
            }
          >
            {t('quality.section')}
            {/* The picker states the quarter on screen but is hidden in print, so the PDF says it here. */}
            {!!report && <span className="hidden print:inline">{` · ${label}`}</span>}
          </SectionHeading>
        )}
        {!!header && !!compare && <div className="mb-3 flex justify-end print:hidden">{compare}</div>}
        {!!report && (
          <p className="-mt-1 text-sm text-muted-foreground">
            {t('quality.auditedOn', { period: label, date: report.generatedOn })}
            {/* The comparison picker is hidden in print too, so the PDF names the compared quarter here. */}
            {isShown && (
              <span className="hidden print:inline">{` · ${t('quality.compare.print', { period: compareTo })}`}</span>
            )}
          </p>
        )}
      </div>
      {body}
    </section>
  );
};

export default QualityTab;
